import { onUnmounted, type Ref, type Reactive } from 'vue';
import { requestInterval, clearRequestInterval, type IntervalID } from 'src/lib/requestAnimationFrame';
import { DEFAULT_DATE_RANGE_DAYS, MSEC_PER_DAY, POLLING_INTERVAL_MS } from 'src/consts/top';
import { isGeoItemMetaComment } from 'src/lib/geoItemHelper';
import { getLocationDispOfKp } from 'src/lib/commentHelper';
import type { CommentIndexParams } from 'src/models/apis/comment/commentRequest';
import type { RecentCommentElement } from 'src/models/comment';
import type { GeoItemsMeta, GIComment } from 'src/models/geoItem';
import type { Tab, TabResources } from 'src/models';
import type { MastersContext } from 'src/models/master';

type RecentCommentContext = {
  startGettingRecentComments: () => Promise<void>;
  refreshCommentTab: () => void;
};

// Top画面の「新着の付箋」タブのポーリング取得。
export function useRecentComment(
  geoItemsMeta: GeoItemsMeta,
  masters: Reactive<MastersContext>,
  tabs: Ref<Tab[]>,
): RecentCommentContext {
  let commentUpdateTimer: IntervalID | null = null;
  onUnmounted(() => {
    clearRequestInterval(commentUpdateTimer);
  });

  const convCommentsAsRecentComments = (comments: GIComment[]): RecentCommentElement[] => {
    // 新着順にソート
    return comments.slice()
      .map(comment => {
        let kpDisp = null;
        if (comment.kpUid) {
          const kpObj = masters.kp.kpMapByKpUid.get(comment.kpUid);
          if (kpObj) {
            kpDisp = getLocationDispOfKp(kpObj, masters.kp.roadNameDispMap);
          }
        }
        return { ...comment, kpDisp };
      })
      .sort((a, b) => {
        return a.ts && b.ts && a.ts < b.ts ? 1 : a.ts && b.ts && a.ts > b.ts ? -1 : 0;
      });
  };
  const initGetRecentCommentsInterval = async(resources: TabResources) => {
    clearRequestInterval(commentUpdateTimer);
    const getParams = (): CommentIndexParams => {
      const now = new Date();
      return {
        ts_from: new Date(now.valueOf() - MSEC_PER_DAY * (DEFAULT_DATE_RANGE_DAYS - 1)),
        ts_to: now,
      };
    };
    const pollRecentComments = async() => {
      const commentMeta = geoItemsMeta.map.comment;
      if (!isGeoItemMetaComment(commentMeta)) return;

      const comments = await commentMeta.manager.getResourcesByParams(getParams());
      resources.elements = convCommentsAsRecentComments(comments);
    };
    await pollRecentComments();
    commentUpdateTimer = requestInterval(pollRecentComments, POLLING_INTERVAL_MS);
  };
  const startGettingRecentComments = async() => {
    const commentTab: Tab = {
      dataName: 'comment',
      label: '新着の付箋',
      resources: {
        ready: false,
        elements: [],
      },
    };
    // 検索中にlabelが初期表示されるようにtabsに入れる
    tabs.value = [commentTab];
    // reactive() に格納後は生のcommentTabではなくProxy経由で更新しないと
    // 再描画がトリガーされない（Vue3のリアクティビティ仕様）。
    // 30秒ごとのポーリング(func)もこのProxy参照を捕捉するため自動更新が効く。
    const commentResources = tabs.value[0].resources;
    await initGetRecentCommentsInterval(commentResources);
    commentResources.ready = true;
  };
  const refreshCommentTab = () => {
    const commentTab = tabs.value.find(e => e.dataName === 'comment');
    if (!commentTab) return;
    void initGetRecentCommentsInterval(commentTab.resources);
  };

  return { startGettingRecentComments, refreshCommentTab };
}
